import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Loading from "../components/Loading";
import FavHeart from "../components/FavHeart";
import ShareButton from "../components/ShareButton";
import CommentsSection from "../components/CommentsSection";
import { ArrowLeft, ExternalLink, Rocket as RocketIcon } from "lucide-react";

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center border-b border-[color:var(--sat-border)] py-2">
      <span className="sat-label">{label}</span>
      <span className="font-mono text-sm text-[color:var(--sat-text)]">{value ?? "—"}</span>
    </div>
  );
}

export default function RocketDetailPage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/rockets/${id}`)
      .then(res => setR(res.data))
      .catch(() => setR(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loading message="ACCESSING VEHICLE SPECIFICATIONS" />;
  if (!r) return <div className="sat-label text-center py-16" data-testid="rocket-not-found">VEHICLE NOT FOUND</div>;

  const mfg = r.manufacturer || {};
  const displayName = r.full_name || r.name;
  return (
    <div className="pb-10" data-testid="page-rocket-detail">
      <Link to="/fleet" className="sat-label inline-flex items-center gap-2 mb-6 hover:sat-cyan" data-testid="back-fleet">
        <ArrowLeft size={14} /> BACK TO FLEET
      </Link>

      <div className="sat-surface sat-brackets p-6 md:p-10 mb-6 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <div className="bg-black flex items-center justify-center p-4 min-h-[220px]">
          {r.image_url ? <img src={r.image_url} alt={displayName} className="max-h-72 object-contain"/> : <RocketIcon size={60} className="text-[color:var(--sat-border-bright)]" />}
        </div>
        <div>
          <div className="sat-label sat-cyan mb-2">{r.family?.toUpperCase() || r.family || "FAMILY"}</div>
          <h1 className="font-display font-bold text-3xl md:text-5xl leading-tight" data-testid="rocket-name">{displayName}</h1>
          {r.description && <p className="text-sm text-[color:var(--sat-text-dim)] mt-4 leading-relaxed">{r.description}</p>}
          <div className="flex flex-wrap gap-3 mt-6">
            <FavHeart kind="rocket" ref_id={r.id} title={displayName} image={r.image_url} size={14} testid="rocket-fav" />
            <ShareButton url={typeof window !== "undefined" ? window.location.href : ""} title={displayName} size={14} testid="rocket-share" />
            <span className="border border-[color:var(--sat-cyan)] px-3 py-2 font-mono text-[10px] tracking-widest sat-cyan">
              {r.reusable ? "REUSABLE" : "EXPENDABLE"}
            </span>
            {r.active !== undefined && (
              <span className="border border-[color:var(--sat-border-bright)] px-3 py-2 font-mono text-[10px] tracking-widest text-[color:var(--sat-text-dim)]">
                {r.active ? "ACTIVE" : "RETIRED"}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <section className="sat-surface p-6" data-testid="specs-section">
          <div className="sat-label sat-cyan mb-4">SPECIFICATIONS</div>
          <Row label="LENGTH" value={r.length ? `${r.length} m` : null} />
          <Row label="DIAMETER" value={r.diameter ? `${r.diameter} m` : null} />
          <Row label="MAX STAGE" value={r.max_stage} />
          <Row label="LAUNCH MASS" value={r.launch_mass ? `${r.launch_mass} t` : null} />
          <Row label="LEO CAPACITY" value={r.leo_capacity ? `${r.leo_capacity} kg` : null} />
          <Row label="GTO CAPACITY" value={r.gto_capacity ? `${r.gto_capacity} kg` : null} />
          <Row label="THRUST" value={r.to_thrust ? `${r.to_thrust} kN` : null} />
          <Row label="LAUNCH COST" value={r.launch_cost ? `$${Number(r.launch_cost).toLocaleString()}` : null} />
        </section>

        <section className="sat-surface p-6" data-testid="flight-record-section">
          <div className="sat-label sat-cyan mb-4">FLIGHT RECORD</div>
          <Row label="TOTAL LAUNCHES" value={r.total_launch_count} />
          <Row label="SUCCESSFUL" value={r.successful_launches} />
          <Row label="FAILED" value={r.failed_launches} />
          <Row label="CONSECUTIVE" value={r.consecutive_successful_launches} />
          <Row label="MAIDEN FLIGHT" value={r.maiden_flight} />
        </section>
      </div>

      <section className="sat-surface p-6 mb-6" data-testid="manufacturer-section">
        <div className="sat-label sat-cyan mb-4">MANUFACTURER</div>
        <div className="flex items-center gap-4 flex-wrap">
          {mfg.logo_url && <img src={mfg.logo_url} alt={mfg.name} className="w-16 h-16 object-contain bg-white p-2" />}
          <div className="flex-1">
            <h3 className="font-display font-bold text-xl">{mfg.name || "—"}</h3>
            <div className="sat-label mt-1">{mfg.country_code || ""} · {mfg.type || ""} · FOUNDED {mfg.founding_year || "—"}</div>
          </div>
        </div>
      </section>

      {(r.wiki_url || r.info_url) && (
        <section className="sat-surface p-6" data-testid="resources-section">
          <div className="sat-label sat-cyan mb-4">RESOURCES</div>
          <div className="flex flex-wrap gap-3">
            {r.wiki_url && <a href={r.wiki_url} target="_blank" rel="noopener noreferrer" className="sat-btn" data-testid="link-wiki">WIKIPEDIA <ExternalLink size={11}/></a>}
            {r.info_url && <a href={r.info_url} target="_blank" rel="noopener noreferrer" className="sat-btn" data-testid="link-info">OFFICIAL INFO <ExternalLink size={11}/></a>}
          </div>
        </section>
      )}

      <div className="mt-6">
        <CommentsSection targetKind="rocket" targetId={String(r.id)} />
      </div>
    </div>
  );
}
